// log-pile client SDK — one dependency-free file meant to be copied into a
// consuming repo (e.g. todoit/shared-utils/logPile.ts) or imported from here.
// It buffers breadcrumbs per flow, builds captures that carry every relevant
// breadcrumb so far, splits what won't fit into follow-up `amend` messages,
// and hands finished messages to a transport (EventBridge in a Lambda,
// sendBeacon in a browser).
//
// Types are duplicated from ../types/capture.ts on purpose so this file has
// no imports — keep them in sync when the wire shape changes.
//
// VERSION: an ISO UTC timestamp, set to "now" on every change to this file in
// the log-pile repo (`date -u +%Y-%m-%dT%H:%M:%SZ`). Each copy in a consuming
// repo then says which revision it is, and every capture carries it as
// `sdkVersion`, so the site shows which copy sent what.
export const LOG_PILE_CLIENT_VERSION = "2026-10-17T21:22:41Z";

export type Level = "debug" | "info" | "warning" | "error" | "fatal";
export type CaptureKind = "error" | "message" | "event";
export type Runtime = "lambda" | "browser" | "node" | "other";

export type Breadcrumb = {
  ts: string;
  seq: number;
  message: string;
  category?: string;
  level?: Level;
  data?: Record<string, unknown>;
};

export type CaptureUser = { id?: string; username?: string; email?: string };
export type CaptureError = { name: string; message: string; stack?: string };

export type CaptureInput = {
  id: string;
  kind: CaptureKind;
  level: Level;
  message: string;
  timestamp: string;
  source: string;
  runtime: Runtime;
  env: string;
  release?: string;
  lambdaName?: string;
  location?: string;
  requestId?: string;
  sdkVersion?: string;
  flowId?: string;
  user?: CaptureUser;
  error?: CaptureError;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string;
  breadcrumbs: Breadcrumb[];
  breadcrumbsTruncated?: boolean;
  breadcrumbsOmitted?: number;
  suppressedBefore?: number;
};

export type AmendInput = { captureId: string; chunk: number; breadcrumbs: Breadcrumb[] };

export type IngestMessage = { type: "capture"; data: CaptureInput } | { type: "amend"; data: AmendInput };

/** Delivers a batch of messages. May be async; failures are reported to onError, never thrown at the caller. */
export type Transport = {
  send: (messages: IngestMessage[]) => void | Promise<void>;
  /** Largest single message this transport can carry, in bytes. */
  maxMessageBytes: number;
};

export type FlowContext = {
  flowId?: string;
  requestId?: string;
  lambdaName?: string;
  location?: string;
  user?: CaptureUser;
  tags?: Record<string, string>;
};

export type LogPileOptions = {
  source: string;
  runtime: Runtime;
  /** The sender's environment: "dev" | "prod" | "local"… Part of the server's keys. */
  env: string;
  transport: Transport;
  release?: string;
  /** Ring buffer size. Default 200. */
  maxBreadcrumbs?: number;
  /** Bytes a message may use; defaults to the transport's maxMessageBytes minus headroom. */
  budgetBytes?: number;
  /**
   * Client-side cap on captures *sent* from this process (a Lambda container,
   * a browser tab). Over any window, the capture is console.logged in a fixed
   * JSON shape instead of sent (so CloudWatch / devtools still have it), and
   * the next capture that gets through carries `suppressedBefore`. Default
   * 3/s, 60/min, 240/h; `false` disables. Amend chunks ride on an allowed
   * capture and aren't counted.
   */
  rateLimit?: RateLimit | false;
  onError?: (error: unknown) => void;
  now?: () => Date;
  randomId?: () => string;
};

export type RateLimit = { perSecond: number; perMinute: number; perHour: number };
export const DEFAULT_RATE_LIMIT: RateLimit = { perSecond: 3, perMinute: 60, perHour: 240 };

// Sliding windows over the send times of the last hour (at most perHour
// entries, so it's tiny). Pure; the SDK owns one per process.
export const createRateLimiter = (limit: RateLimit) => {
  let sent: number[] = [];
  return {
    /** Which window (if any) blocks a send at `nowMs`; records the send when allowed. */
    tryTake: (nowMs: number): undefined | "second" | "minute" | "hour" => {
      sent = sent.filter((t) => nowMs - t < 3_600_000);
      if (sent.length >= limit.perHour) return "hour";
      let minute = 0;
      let second = 0;
      for (const t of sent) {
        if (nowMs - t < 60_000) minute++;
        if (nowMs - t < 1_000) second++;
      }
      if (minute >= limit.perMinute) return "minute";
      if (second >= limit.perSecond) return "second";
      sent.push(nowMs);
      return undefined;
    },
  };
};

export type CaptureOptions = {
  kind?: CaptureKind;
  level?: Level;
  message?: string;
  error?: unknown;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string;
  user?: CaptureUser;
};

const BUDGET_HEADROOM = 0.9;
// Mirrors backend/utils/validate.ts LIMITS: anything over is rejected server
// side, so clamp here rather than lose the capture.
const MAX_MESSAGE = 4_000;
const MAX_STACK = 30_000;
const MAX_CRUMB_MESSAGE = 2_000;
const MAX_CRUMB_DATA = 8_000; // serialised
const clamp = (s: string | undefined, max: number) => (s !== undefined && s.length > max ? `${s.slice(0, max - 1)}…` : s);

export const byteLength = (value: unknown): number => {
  const json = JSON.stringify(value) ?? "";
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(json).length;
  // Node without TextEncoder (never, in practice): UTF-8 upper bound.
  return json.length * 3;
};

// Oversized breadcrumb data is replaced by a truncated preview rather than
// dropped: with message and data both capped, a single crumb is always far
// under any transport's limit, so splitBreadcrumbs never has to omit one.
const clampData = (data: Record<string, unknown> | undefined) => {
  if (data === undefined) return undefined;
  const json = safeStringify(data);
  if (json.length <= MAX_CRUMB_DATA) return data;
  return { _truncated: true, _originalChars: json.length, preview: json.slice(0, MAX_CRUMB_DATA - 200) };
};

const defaultRandomId = (): string => {
  const c = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const toCaptureError = (error: unknown): CaptureError => {
  const raw = (): CaptureError => {
    if (error instanceof Error) {
      return { name: error.name || "Error", message: error.message || String(error), stack: error.stack };
    }
    if (typeof error === "object" && error && "message" in error) {
      const e = error as { name?: unknown; message?: unknown; stack?: unknown };
      return {
        name: typeof e.name === "string" ? e.name : "Error",
        message: String(e.message),
        stack: typeof e.stack === "string" ? e.stack : undefined,
      };
    }
    return { name: "Error", message: typeof error === "string" ? error : safeStringify(error) };
  };
  const e = raw();
  return { name: e.name.slice(0, 200), message: clamp(e.message, MAX_MESSAGE) as string, stack: clamp(e.stack, MAX_STACK) };
};

const safeStringify = (value: unknown) => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};

/**
 * Fits the newest breadcrumbs inline up to `budget` bytes (given the capture's
 * size without crumbs), and packs everything older into amend chunks, each
 * under `budget` — greedily, so two crumbs that don't fit together go in
 * separate chunks, down to one crumb per chunk. Crumbs stay in chronological
 * order in every piece. A single crumb larger than the whole budget can't be
 * sent at all and is counted as omitted; addBreadcrumb's clamps make that
 * unreachable in practice.
 */
export const splitBreadcrumbs = (
  crumbs: Breadcrumb[],
  budget: number,
  baseBytes: number,
): { inline: Breadcrumb[]; chunks: Breadcrumb[][]; omitted: number } => {
  const sizes = crumbs.map((c) => byteLength(c) + 1);
  let used = baseBytes;
  let start = crumbs.length;
  while (start > 0 && used + sizes[start - 1] <= budget) {
    used += sizes[start - 1];
    start--;
  }
  const inline = crumbs.slice(start);
  const rest = crumbs.slice(0, start);
  const chunks: Breadcrumb[][] = [];
  let omitted = 0;
  const chunkBase = 200; // envelope for {captureId, chunk, breadcrumbs: []}
  let current: Breadcrumb[] = [];
  let currentBytes = chunkBase;
  for (let i = 0; i < rest.length; i++) {
    const size = sizes[i];
    if (chunkBase + size > budget) {
      omitted++;
      continue;
    }
    if (currentBytes + size > budget) {
      chunks.push(current);
      current = [];
      currentBytes = chunkBase;
    }
    current.push(rest[i]);
    currentBytes += size;
  }
  if (current.length) chunks.push(current);
  return { inline, chunks, omitted };
};

export const createLogPile = (options: LogPileOptions) => {
  const now = options.now ?? (() => new Date());
  const randomId = options.randomId ?? defaultRandomId;
  const maxBreadcrumbs = options.maxBreadcrumbs ?? 200;
  const budget = options.budgetBytes ?? Math.floor(options.transport.maxMessageBytes * BUDGET_HEADROOM);
  const onError = options.onError ?? ((e: unknown) => console.warn("log-pile: transport failed", e));

  let crumbs: Breadcrumb[] = [];
  let seq = 0;
  let context: FlowContext = {};
  const pending = new Set<Promise<void>>();
  const limiter = options.rateLimit === false ? undefined : createRateLimiter(options.rateLimit ?? DEFAULT_RATE_LIMIT);
  let suppressedSinceSend = 0;
  // Console fallback for suppressed captures is itself bounded: the full
  // capture (minus breadcrumbs) for the first `perMinute` suppressed in a
  // minute, then one counter line per 100 — so a 20/s loop can't turn
  // CloudWatch into the bill.
  let suppressedLogMinute = "";
  let suppressedLoggedThisMinute = 0;
  const logSuppressed = (capture: CaptureInput, window: string) => {
    const minute = capture.timestamp.slice(0, 16);
    if (minute !== suppressedLogMinute) {
      suppressedLogMinute = minute;
      suppressedLoggedThisMinute = 0;
    }
    suppressedLoggedThisMinute++;
    const verboseCap = (options.rateLimit || DEFAULT_RATE_LIMIT).perMinute;
    if (suppressedLoggedThisMinute <= verboseCap) {
      const { breadcrumbs, ...rest } = capture;
      console.log(JSON.stringify({ logPile: "suppressed", window, breadcrumbCount: breadcrumbs.length, capture: rest }));
    } else if (suppressedLoggedThisMinute % 100 === 0) {
      console.log(JSON.stringify({ logPile: "suppressed", window, suppressedThisMinute: suppressedLoggedThisMinute, source: capture.source, message: capture.message }));
    }
  };

  // A transport failure (bad key → 401, missing IAM, bus down…) never reaches
  // the caller: it goes to onError (default: console.warn) and the messages
  // themselves are console.logged in the same fixed shape the rate limiter
  // uses, minus breadcrumbs — so a misconfigured deployment still leaves the
  // capture in CloudWatch / devtools instead of losing it.
  const undelivered = (messages: IngestMessage[], e: unknown) => {
    onError(e);
    for (const m of messages) {
      const data = m.type === "capture" ? { ...m.data, breadcrumbs: undefined, breadcrumbCount: m.data.breadcrumbs.length } : m.data;
      console.log(JSON.stringify({ logPile: "undelivered", reason: toCaptureError(e).message.slice(0, 500), [m.type]: data }));
    }
  };
  const deliver = (messages: IngestMessage[]) => {
    if (!messages.length) return;
    try {
      const result = options.transport.send(messages);
      if (result && typeof (result as Promise<void>).then === "function") {
        const p = (result as Promise<void>).catch((e) => undelivered(messages, e)).finally(() => pending.delete(p));
        pending.add(p);
      }
    } catch (e) {
      undelivered(messages, e);
    }
  };

  const api = {
    /**
     * Call at the start of each unit of work (a Lambda invocation, a page
     * view). Throws away every breadcrumb and all user/flow context from
     * whatever ran before in this process — the whole point in a warm Lambda
     * — and starts a fresh flow whose context every capture inherits. There
     * is no time-based relevance: what's in the buffer since the last reset
     * is, by definition, this flow.
     */
    reset: (flow: FlowContext = {}) => {
      crumbs = [];
      seq = 0;
      context = { ...flow, flowId: flow.flowId ?? randomId() };
      return context.flowId as string;
    },

    setUser: (user: CaptureUser | undefined) => {
      context = { ...context, user };
    },

    setContext: (patch: Partial<FlowContext>) => {
      context = { ...context, ...patch };
    },

    getFlowId: () => context.flowId,

    addBreadcrumb: (crumb: { message: string; category?: string; level?: Level; data?: Record<string, unknown> }) => {
      const entry: Breadcrumb = {
        ts: now().toISOString(),
        seq: seq++,
        ...crumb,
        message: clamp(crumb.message, MAX_CRUMB_MESSAGE) as string,
        data: clampData(crumb.data),
      };
      crumbs.push(entry);
      if (crumbs.length > maxBreadcrumbs) crumbs.splice(0, crumbs.length - maxBreadcrumbs);
      return entry;
    },

    /**
     * Sends a capture carrying every relevant breadcrumb so far. Returns the
     * capture id. Then drops a breadcrumb about the capture itself, so a
     * later capture in the same flow shows "capture 1 happened here" in its
     * timeline — and still carries all of capture 1's crumbs too.
     */
    capture: (opts: CaptureOptions): string => {
      const id = randomId();
      const error = opts.error === undefined ? undefined : toCaptureError(opts.error);
      const kind: CaptureKind = opts.kind ?? (error ? "error" : "message");
      const level: Level = opts.level ?? (error ? "error" : "info");
      const message = clamp(opts.message ?? error?.message ?? "(no message)", MAX_MESSAGE) as string;
      const base: CaptureInput = {
        id,
        kind,
        level,
        message,
        timestamp: now().toISOString(),
        source: options.source,
        runtime: options.runtime,
        env: options.env,
        release: options.release,
        lambdaName: context.lambdaName ?? defaultLambdaName(),
        location: context.location ?? defaultLocation(),
        requestId: context.requestId,
        sdkVersion: LOG_PILE_CLIENT_VERSION,
        flowId: context.flowId,
        user: opts.user ?? context.user,
        error,
        tags: { ...context.tags, ...opts.tags },
        extra: opts.extra,
        fingerprint: opts.fingerprint,
        breadcrumbs: [],
      };
      const blocked = limiter?.tryTake(now().getTime());
      if (blocked) {
        suppressedSinceSend++;
        logSuppressed({ ...base, breadcrumbs: crumbs.slice() }, blocked);
        api.addBreadcrumb({
          category: "capture",
          level,
          message: `suppressed (${blocked} rate limit) ${kind}: ${message}`.slice(0, 500),
          data: { captureId: id },
        });
        return id;
      }
      const suppressedBefore = suppressedSinceSend || undefined;
      suppressedSinceSend = 0;
      const { inline, chunks, omitted } = splitBreadcrumbs(crumbs.slice(), budget, byteLength({ ...base, suppressedBefore }));
      const capture: CaptureInput = {
        ...base,
        suppressedBefore,
        breadcrumbs: inline,
        ...(chunks.length || omitted
          ? {
              breadcrumbsTruncated: true,
              breadcrumbsOmitted: omitted + chunks.reduce((n, c) => n + c.length, 0),
            }
          : {}),
      };
      // Capture first, then its amendments, one message each so none can
      // exceed the transport's limit.
      deliver([{ type: "capture", data: capture }]);
      chunks.forEach((breadcrumbs, i) =>
        deliver([{ type: "amend", data: { captureId: id, chunk: i + 1, breadcrumbs } }]),
      );
      api.addBreadcrumb({
        category: "capture",
        level,
        message: `captured ${kind}: ${message}`.slice(0, 500),
        data: { captureId: id },
      });
      return id;
    },

    captureException: (error: unknown, opts: Omit<CaptureOptions, "error"> = {}) =>
      api.capture({ ...opts, error, kind: "error", level: opts.level ?? "error" }),

    captureMessage: (message: string, level: Level = "info", opts: Omit<CaptureOptions, "message" | "level"> = {}) =>
      api.capture({ ...opts, message, level, kind: "message" }),

    captureEvent: (message: string, opts: Omit<CaptureOptions, "message" | "kind"> = {}) =>
      api.capture({ ...opts, message, kind: "event", level: opts.level ?? "info" }),

    /** Attach more breadcrumbs to a capture already sent (any chunk numbering ≥ 1000 is yours). */
    amend: (captureId: string, breadcrumbs: Breadcrumb[], chunk = 1000) => {
      const { chunks, omitted } = splitBreadcrumbs(breadcrumbs, budget, budget); // all go to chunks
      if (omitted) onError(new Error(`log-pile: ${omitted} breadcrumb(s) too large to amend`));
      chunks.forEach((crumbsChunk, i) =>
        deliver([{ type: "amend", data: { captureId, chunk: chunk + i, breadcrumbs: crumbsChunk } }]),
      );
    },

    /** Resolve once every in-flight transport call settles. Call before a Lambda returns. */
    flush: async () => {
      await Promise.allSettled(Array.from(pending));
    },
  };
  return api;
};

export type LogPile = ReturnType<typeof createLogPile>;

const defaultLocation = () => {
  const loc = (globalThis as any).location;
  return typeof loc?.href === "string" ? loc.href : undefined;
};

// Lambda sets this in every function's environment, so a capture from a
// handler that never called reset()/withLogPile still names its function.
const defaultLambdaName = (): string | undefined => {
  const env = (globalThis as any).process?.env;
  return typeof env?.AWS_LAMBDA_FUNCTION_NAME === "string" ? env.AWS_LAMBDA_FUNCTION_NAME : undefined;
};

// ---------------------------------------------------------------------------
// Transports

/**
 * EventBridge, for Lambdas and other backends — onto the account's shared
 * per-stage bus (`${ssm:/doridium/{stage}/bus/name}`). `putEvents` is injected
 * so this file needs no AWS SDK import:
 *   import { EventBridge } from "@aws-sdk/client-eventbridge";
 *   const eb = new EventBridge();
 *   eventBridgeTransport({ busName: process.env.SHARED_BUS!, source: "todoit-site-backend",
 *                          putEvents: (p) => eb.putEvents(p) })
 * The producer's role needs `events:PutEvents` on the bus arn
 * (`${ssm:/doridium/{stage}/bus/arn}`). `source` becomes the event's Source
 * (AWS-side tracking; log-pile stores the capture's own `source` field), the
 * detail-type is logpile.captureError / .captureMessage / .captureEvent /
 * .amend, and `detail` is `{ data: <payload>, meta: { sdkVersion, sentAt } }`.
 */
export const eventBridgeTransport = (opts: {
  busName: string;
  /** The sending service's name, e.g. "todoit-site-backend" — the event's Source. */
  source: string;
  putEvents: (params: {
    Entries: Array<{ EventBusName: string; Source: string; DetailType: string; Detail: string }>;
  }) => Promise<{ FailedEntryCount?: number; Entries?: Array<{ ErrorCode?: string; ErrorMessage?: string }> } | unknown>;
}): Transport => ({
  // EventBridge: 256 KB per entry including envelope.
  maxMessageBytes: 240_000,
  send: async (messages) => {
    const detailType = (m: IngestMessage) =>
      m.type === "amend"
        ? "logpile.amend"
        : m.data.kind === "error"
          ? "logpile.captureError"
          : m.data.kind === "message"
            ? "logpile.captureMessage"
            : "logpile.captureEvent";
    const sentAt = new Date().toISOString();
    const entries = messages.map((m) => ({
      EventBusName: opts.busName,
      Source: opts.source,
      DetailType: detailType(m),
      Detail: JSON.stringify({ data: m.data, meta: { sdkVersion: LOG_PILE_CLIENT_VERSION, sentAt } }),
    }));
    // 10 entries per PutEvents call. A rejected entry (too large, bus
    // missing…) does NOT throw — it comes back in the result — so check.
    for (let i = 0; i < entries.length; i += 10) {
      const result = (await opts.putEvents({ Entries: entries.slice(i, i + 10) })) as
        | { FailedEntryCount?: number; Entries?: Array<{ ErrorCode?: string; ErrorMessage?: string }> }
        | undefined;
      if (result?.FailedEntryCount) {
        const failures = (result.Entries ?? []).filter((e) => e.ErrorCode).map((e) => `${e.ErrorCode}: ${e.ErrorMessage}`);
        throw new Error(`log-pile: ${result.FailedEntryCount} event(s) rejected by EventBridge — ${failures.join("; ")}`);
      }
    }
  },
});

/**
 * Browser transport. Two modes, chosen per site from its env file:
 *   "beacon" (default) — `navigator.sendBeacon`: fire-and-forget, survives
 *     page unload, the reply is never read, so a rejected key or body is
 *     swallowed silently.
 *   "fetch" — `fetch` with `keepalive` (also survives unload, same ~64 KB
 *     cap) and the reply IS read: a 401 from a wrong key or a 4xx from a
 *     rejected body goes to onError and the capture is console-logged as
 *     undelivered. Use this while setting a site up.
 * Body is text/plain so the browser skips the CORS preflight; the public key
 * in the body tells the server which source this is.
 */
export type BeaconMode = "beacon" | "fetch";
export const beaconTransport = (opts: {
  url: string;
  key: string;
  mode?: BeaconMode;
  fetchImpl?: typeof fetch;
  sendBeaconImpl?: (url: string, data: Blob) => boolean;
}): Transport => ({
  maxMessageBytes: 50_000,
  send: (messages) => {
    const body = JSON.stringify({ key: opts.key, items: messages });
    const f: typeof fetch | undefined = opts.fetchImpl ?? (globalThis as any).fetch;
    const nav = (globalThis as any).navigator;
    const beacon: ((url: string, data: Blob) => boolean) | undefined =
      opts.sendBeaconImpl ?? (nav?.sendBeacon ? nav.sendBeacon.bind(nav) : undefined);
    const mode: BeaconMode = opts.mode ?? "beacon";

    if (mode === "beacon" && beacon) {
      if (!beacon(opts.url, new Blob([body], { type: "text/plain" }))) {
        throw new Error("log-pile: sendBeacon refused the payload (queue full?)");
      }
      return;
    }
    if (!f) throw new Error("log-pile: no fetch or sendBeacon available");
    return f(opts.url, { method: "POST", body, headers: { "content-type": "text/plain" }, keepalive: true }).then(
      async (res) => {
        if (mode === "beacon" || res.ok) return; // beacon mode never reads the reply
        const text = await res.text().catch(() => "");
        throw new Error(`log-pile ingest responded ${res.status}${text ? `: ${text.slice(0, 200)}` : ""}`);
      },
    );
  },
});

/** Logs messages to the console — local development. */
export const consoleTransport = (): Transport => ({
  maxMessageBytes: 240_000,
  send: (messages) => {
    for (const m of messages) console.log(`[log-pile ${m.type}]`, JSON.stringify(m.data, null, 2));
  },
});

// ---------------------------------------------------------------------------
// Helpers

/**
 * Wraps a Lambda handler: resets per invocation (awsRequestId as the flowId,
 * so a warm container never leaks crumbs or user context across requests),
 * captures an unhandled throw, and flushes before returning either way.
 */
export const withLogPile =
  <Event, Result>(
    logPile: LogPile,
    handler: (event: Event, context: any) => Promise<Result>,
    getFlow?: (event: Event, context: any) => Partial<FlowContext>,
  ) =>
  async (event: Event, context: any): Promise<Result> => {
    logPile.reset({
      flowId: context?.awsRequestId,
      requestId: context?.awsRequestId,
      lambdaName: context?.functionName,
      ...getFlow?.(event, context),
    });
    try {
      return await handler(event, context);
    } catch (e) {
      logPile.captureException(e, { level: "fatal", message: `unhandled: ${toCaptureError(e).message}` });
      throw e;
    } finally {
      await logPile.flush();
    }
  };

/** Browser: window errors and unhandled rejections become captures. Returns an uninstall function. */
export const installBrowserHandlers = (logPile: LogPile) => {
  const w = globalThis as any;
  if (!w.addEventListener) return () => {};
  const onError = (ev: any) => {
    logPile.captureException(ev?.error ?? ev?.message ?? "window.onerror", {
      extra: { filename: ev?.filename, lineno: ev?.lineno, colno: ev?.colno },
    });
  };
  const onRejection = (ev: any) => {
    logPile.captureException(ev?.reason ?? "unhandledrejection", { tags: { unhandled: "rejection" } });
  };
  w.addEventListener("error", onError);
  w.addEventListener("unhandledrejection", onRejection);
  return () => {
    w.removeEventListener("error", onError);
    w.removeEventListener("unhandledrejection", onRejection);
  };
};
